import { forwardRef, type ReactNode, type TextareaHTMLAttributes, type InputHTMLAttributes } from 'react'
import { cn } from '../lib/cn'

/**
 * The labelled form row. The label column is fixed and the control column is
 * fluid, so every control on a page sits on the same x however long its
 * label runs. Hint under label, error as its own line.
 */
export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
  className,
}: {
  label: string
  hint?: string
  error?: string
  htmlFor?: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="text-ui font-medium text-foreground">
        {label}
      </label>
      {children}
      {error ? (
        <p role="alert" className="text-caption text-danger">
          {error}
        </p>
      ) : hint ? (
        <p className="text-caption text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  )
}

/**
 * A text input wearing the app's field treatment. Only `text` and `password`
 * are offered: the other native types bring browser validation bubbles and
 * spinner affordances this design system has no drawing for.
 *
 * `underline` is the minimal treatment — no box, one hairline, the ink line
 * growing on focus — for pages that read as documents, like onboarding.
 */
export const TextField = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { type?: 'text' | 'password'; variant?: 'boxed' | 'underline' }>(
  function TextField({ type = 'text', variant = 'boxed', className, ...rest }, ref) {
    return (
      <input
        ref={ref}
        type={type}
        autoComplete="off"
        className={cn(variant === 'underline' ? 'wf-field-underline' : 'wf-field', className)}
        {...rest}
      />
    )
  },
)

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { variant?: 'boxed' | 'underline' }>(function TextArea(
  { className, rows = 3, variant = 'boxed', ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(variant === 'underline' ? 'wf-field-underline' : 'wf-field', className)}
      {...rest}
    />
  )
})
